/**
 * Activities dashboard — effort as the thing being managed.
 *
 * Every other screen treats activity as an input to revenue. Here it is the
 * subject: what the team did, when, what came back.
 *
 * The coverage notes are not boilerplate. This table's gaps are specific and
 * measurable — most rows carry no `activity_type`, GPS is a 0/0 sentinel, 76%
 * of completed tasks have no completion timestamp. Rendering those as zeros
 * would read as "the team stopped working", so each panel states what it can
 * and cannot see.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { EChartsOption } from "echarts";
import { Info } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { api } from "@/api/client";
import { Chart } from "@/components/charts/Chart";
import { Disclosure } from "@/components/cards/Disclosure";
import { KpiCard } from "@/components/cards/KpiCard";
import { DataTable, type Column } from "@/components/tables/DataTable";
import { STAGES } from "@/components/timeline/stages";
import { useFilterStore } from "@/store/filters";
import type { AnalyticsEnvelope, Metadata, Quality } from "@/types/api";
import { count, pct } from "@/utils/format";

interface Kind {
  label: string; stage: number; activities: number; positive: number;
  commitments: number; completed: number;
  response_rate_pct: number | null; completed_pct: number | null;
}
interface Rhythm { label: string; activities: number; pct: number | null }
interface Person {
  person_key: string; name: string; activities: number; clients: number;
  response_rate_pct: number | null; per_working_day: number;
}
interface Summary {
  activities: number; completed: number; completed_pct: number | null;
  clients_touched: number; people_active: number; positive_responses: number;
  commitments: number; information: number;
  followups_due: number; followups_missed: number; followups_unknown: number;
  followup_compliance_pct: number | null; per_working_day: number;
}

export function ActivityDashboard() {
  const navigate = useNavigate();
  const params = useFilterStore((s) => s.queryParams());
  const { data, isLoading } = useQuery({
    queryKey: ["activity-dashboard", params],
    queryFn: async () => {
      const { data } = await api.get<AnalyticsEnvelope<Summary>>(
        "/analytics/behaviour/activities", { params });
      return data;
    },
    staleTime: 300_000,
  });

  const s = data?.summary;
  const kinds = (data?.breakdown ?? []) as unknown as Kind[];
  const rhythm = (data?.trend ?? []) as unknown as Rhythm[];
  const people = (data?.insights ?? []) as unknown as Person[];

  const rhythmOption = useMemo<EChartsOption>(() => ({
    tooltip: {
      trigger: "axis", backgroundColor: "#16211F", borderWidth: 0,
      textStyle: { color: "#F6F8F7", fontSize: 12 },
      formatter: (raw: unknown) => {
        const rows = raw as Array<{ dataIndex: number }>;
        const r = rhythm[rows[0]?.dataIndex ?? 0];
        return r ? `<strong>${r.label}</strong><br/>${count(r.activities)} · ${r.pct}%` : "";
      },
    },
    grid: { left: 8, right: 16, top: 16, bottom: 8, containLabel: true },
    xAxis: { type: "category", data: rhythm.map((r) => r.label.slice(0, 3)) },
    yAxis: { type: "value" },
    series: [{
      type: "bar", data: rhythm.map((r) => r.activities),
      itemStyle: { color: "#2563C9", borderRadius: [4, 4, 0, 0] },
      barMaxWidth: 40,
    }],
  }), [rhythm]);

  const kindColumns = useMemo<Column<Kind>[]>(() => [
    {
      key: "label", header: "Activity", width: "230px",
      render: (r) => {
        const stage = STAGES.find((x) => x.n === r.stage);
        return (
          <span className="inline-flex items-center gap-2 min-w-0">
            <span className="w-2 h-2 rounded-sm shrink-0"
                  style={{ background: stage?.color ?? "#5D6E6A" }} />
            <span className="truncate">{r.label}</span>
          </span>
        );
      },
    },
    {
      key: "stage", header: "Stage", width: "120px",
      render: (r) => {
        const stage = STAGES.find((x) => x.n === r.stage);
        return <span className="text-[11.5px] text-ink-muted">
          {stage ? `${stage.n}. ${stage.name}` : "—"}
        </span>;
      },
      value: (r) => r.stage,
    },
    { key: "activities", header: "Activities", align: "right",
      value: (r) => r.activities },
    {
      key: "response_rate_pct", header: "Positive response", align: "right",
      render: (r) => (r.response_rate_pct === 0
        ? <span className="text-ink-faint"
                title="Response is only recorded on rows that carry an activity_type; these do not.">
            not recorded
          </span>
        : pct(r.response_rate_pct)),
      value: (r) => r.response_rate_pct,
    },
    { key: "commitments", header: "Commitments", align: "right",
      value: (r) => r.commitments },
    {
      key: "completed_pct", header: "Completed", align: "right",
      render: (r) => pct(r.completed_pct), value: (r) => r.completed_pct,
    },
  ], []);

  const peopleColumns = useMemo<Column<Person>[]>(() => [
    { key: "name", header: "Person", width: "220px" },
    { key: "activities", header: "Activities", align: "right",
      value: (r) => r.activities },
    { key: "per_working_day", header: "Per working day", align: "right",
      value: (r) => r.per_working_day },
    { key: "clients", header: "Clients", align: "right", value: (r) => r.clients },
    {
      key: "response_rate_pct", header: "Positive response", align: "right",
      render: (r) => pct(r.response_rate_pct), value: (r) => r.response_rate_pct,
    },
  ], []);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display font-bold text-[22px] sm:text-[24px] tracking-tight">
          Activities
        </h1>
        <p className="text-[13.5px] text-ink-muted mt-0.5 max-w-3xl">
          What the team did, when, and what came back — effort in its own right
          rather than as an input to revenue.
        </p>
      </div>

      {isLoading && <div className="skeleton h-96" />}

      {!isLoading && s && (
        <>
          <div className="grid gap-3"
               style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))" }}>
            <KpiCard label="Activities" value={count(s.activities)}
                     footnote={`${s.per_working_day} per working day`} />
            <KpiCard label="Information received" value={count(s.information)}
                     footnote="`New Leads` — the funnel's top" />
            <KpiCard label="Clients touched" value={count(s.clients_touched)} />
            <KpiCard label="People active" value={count(s.people_active)} />
            <KpiCard label="Completed" value={pct(s.completed_pct)} />
            <KpiCard label="Follow-up compliance"
                     value={pct(s.followup_compliance_pct)}
                     footnote={`${count(s.followups_unknown)} with no outcome, excluded`} />
          </div>

          <div className="grid gap-4"
               style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))" }}>
            <section className="card p-4">
              <h2 className="font-display font-semibold text-[15px]">
                Weekly rhythm
              </h2>
              <p className="text-[12px] text-ink-muted mb-2">
                Which days the work actually lands on.
              </p>
              <Chart option={rhythmOption} height={240} />
            </section>

            <section className="card p-4">
              <h2 className="font-display font-semibold text-[15px]">
                Response and commitment
              </h2>
              <p className="text-[12px] text-ink-muted mb-3">
                Of {count(s.activities)} activities, {count(s.positive_responses)}{" "}
                drew a positive response and {count(s.commitments)} secured a
                commitment.
              </p>
              <dl className="space-y-2">
                {([["Positive responses", s.positive_responses],
                   ["Commitments", s.commitments],
                   ["Follow-ups due", s.followups_due],
                   ["Follow-ups missed", s.followups_missed]] as const).map(
                  ([label, value]) => (
                    <div key={label} className="flex items-center gap-3">
                      <dt className="w-40 shrink-0 text-[12.5px] text-ink-muted">
                        {label}
                      </dt>
                      <dd className="flex-1 flex items-center gap-2">
                        <span className="flex-1 h-1.5 rounded-full bg-surface-alt
                                         overflow-hidden">
                          <span className="block h-full rounded-full bg-petrol"
                                style={{ width: `${Math.min(100,
                                  (value / Math.max(1, s.activities)) * 100)}%` }} />
                        </span>
                        <span className="font-mono tnum text-[12.5px] w-20 text-right">
                          {count(value)}
                        </span>
                      </dd>
                    </div>
                  ))}
              </dl>
            </section>
          </div>

          <section className="space-y-2">
            <h2 className="font-display font-semibold text-[16px]">
              By activity, in journey order
            </h2>
            <DataTable<Kind>
              rows={kinds} columns={kindColumns} rowKey={(r) => r.label}
              exportName="activity-types" searchable={false}
              footnote="Labels are the business meaning of the source's task categories; a blank response rate means that row type does not record one."
            />
          </section>

          <section className="space-y-2">
            <h2 className="font-display font-semibold text-[16px]">
              Busiest people
            </h2>
            <DataTable<Person>
              rows={people} columns={peopleColumns}
              rowKey={(r) => r.person_key}
              onRowClick={(r) => navigate(`/employees/${encodeURIComponent(r.person_key)}`)}
              exportName="activity-by-person"
              footnote="Volume is not performance — see Employee comparison for output against peers."
            />
          </section>

          {data?.quality?.notes?.length ? (
            <div className="card p-4 space-y-1.5">
              <div className="label flex items-center gap-1.5">
                <Info size={12} /> what this screen can and cannot see
              </div>
              {data.quality.notes.map((note) => (
                <p key={note} className="text-[12.5px] text-ink-soft">{note}</p>
              ))}
            </div>
          ) : null}

          <Disclosure quality={data?.quality as Quality}
                      metadata={data?.metadata as Metadata} />
        </>
      )}
    </div>
  );
}
